import { FC, useState } from 'react'
import {
  BooleanInput, CreateBase, DeleteWithConfirmButton, EditBase,
  SimpleForm, TextInput, required, useGetList, useNotify,
  useRecordContext, useRefresh, useTranslate,
} from 'react-admin'
import { useWatch } from 'react-hook-form'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import EditIcon from '@mui/icons-material/Edit'
import DnsIcon from '@mui/icons-material/Dns'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, Typography,
} from '@mui/material'
import { ApplicationEnvironmentOut, ApplicationOut } from '../../types/apiRequests'
import { SaveOnlyToolbar } from '../../components'
import { border } from '../../layout/theme/border'
import { xxllncColor } from '../../layout/theme/color'

const AuthConfigFieldsForEnvironment: FC = () => {
  const authType = useWatch({ name: '_auth_type' }) as string | undefined

  if (authType === 'custom_headers') return null

  if (authType === 'oauth_client_credentials') {
    return (
      <>
        <TextInput
          source="auth_config.token_url"
          label="data.tokenUrl"
          fullWidth
          validate={required()}
        />
        <TextInput
          source="auth_config.client_id"
          label="data.clientId"
          fullWidth
          validate={required()}
        />
        <TextInput
          source="auth_config.client_secret"
          label="data.clientSecret"
          fullWidth
          validate={required()}
          type="password"
        />
        <TextInput
          source="auth_config.audience"
          label="data.audience"
          fullWidth
          validate={required()}
        />
      </>
    )
  }

  if (authType === 'client_certificate') {
    return (
      <>
        <TextInput
          source="auth_config.certificate_pem"
          label="data.certificatePem"
          fullWidth
          multiline
          validate={required()}
          helperText="data.certificatePemHelp"
        />
        <TextInput
          source="auth_config.private_key_pem"
          label="data.privateKeyPem"
          fullWidth
          validate={required()}
          type="password"
          helperText="data.privateKeyPemHelp"
        />
      </>
    )
  }

  return null
}

const SectionHeader: FC<{ title: string }> = ({ title }) => (
  <Typography
    variant="overline"
    sx={{
      display: 'block',
      color: 'text.secondary',
      fontWeight: 700,
      fontSize: '0.7rem',
      letterSpacing: '0.08em',
      mt: 2,
      mb: 1,
      pb: 0.5,
      borderBottom: `1px solid ${border.light}`,
    }}
  >
    {title}
  </Typography>
)

const EnvironmentForm: FC<{
  applicationId: string;
  authType: string;
  defaultValues?: Record<string, unknown>;
}> = ({ applicationId, authType, defaultValues }) => {
  const translate = useTranslate()
  const hasAuthFields = authType !== 'custom_headers'

  return (
    <SimpleForm
      defaultValues={{
        // eslint-disable-next-line camelcase
        application_id: applicationId,
        // _auth_type drives conditional rendering; submitted but ignored by Pydantic.
        // eslint-disable-next-line camelcase
        _auth_type: authType,
        ...defaultValues,
      }}
      toolbar={<SaveOnlyToolbar alwaysEnable />}
    >
      <SectionHeader title={translate('data.identitySection')} />
      <TextInput source="display_name" label="data.displayName" fullWidth validate={required()} />
      <TextInput source="description" label="data.description" fullWidth multiline />
      <TextInput source="base_url" label="data.baseUrl" fullWidth />
      <BooleanInput source="enabled" label="data.enabled" defaultValue={true} />
      {hasAuthFields && (
        <>
          <SectionHeader title={translate('data.authenticationSection')} />
          <AuthConfigFieldsForEnvironment />
        </>
      )}
    </SimpleForm>
  )
}

const CreateEnvironmentDialog: FC<{
  open: boolean;
  onClose: () => void;
  applicationId: string;
  authType: string;
}> = ({ open, onClose, applicationId, authType }) => {
  const refresh = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()

  const onSuccess = () => {
    onClose()
    notify(translate('data.environmentCreated'), { type: 'info' })
    refresh()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{translate('data.addEnvironment')}</DialogTitle>
      <DialogContent>
        <CreateBase resource="application_environment" redirect={false} mutationOptions={{ onSuccess }}>
          <EnvironmentForm applicationId={applicationId} authType={authType} />
        </CreateBase>
      </DialogContent>
    </Dialog>
  )
}

const EditEnvironmentDialog: FC<{
  open: boolean;
  onClose: () => void;
  environment: ApplicationEnvironmentOut | null;
  applicationId: string;
  authType: string;
}> = ({ open, onClose, environment, applicationId, authType }) => {
  const refresh = useRefresh()
  const notify = useNotify()
  const translate = useTranslate()

  if (!environment) return null

  const onSuccess = () => {
    onClose()
    notify(translate('data.environmentUpdated'), { type: 'info' })
    refresh()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{translate('data.editEnvironment')}</DialogTitle>
      <DialogContent>
        <EditBase
          resource="application_environment"
          id={environment.id}
          mutationMode="pessimistic"
          redirect={false}
          mutationOptions={{ onSuccess }}
        >
          <EnvironmentForm
            applicationId={applicationId}
            authType={authType}
            defaultValues={{
              // eslint-disable-next-line camelcase
              display_name: environment.display_name,
              description: environment.description,
              // eslint-disable-next-line camelcase
              base_url: environment.base_url,
              // eslint-disable-next-line camelcase
              auth_config: environment.auth_config,
              enabled: environment.enabled,
            }}
          />
        </EditBase>
      </DialogContent>
    </Dialog>
  )
}

const truncateUrl = (url: string | null | undefined, maxLen = 50): string => {
  if (!url) return '-'
  return url.length > maxLen ? `${url.slice(0, maxLen)}...` : url
}

const EnvironmentCard: FC<{
  env: ApplicationEnvironmentOut;
  onEdit: (env: ApplicationEnvironmentOut) => void;
  onDeleted: () => void;
}> = ({ env, onEdit, onDeleted }) => {
  const translate = useTranslate()

  return (
    <Box
      sx={{
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'space-between',
        'p': 2.5,
        'borderRadius': border.card.radius,
        'border': `1px solid ${border.light}`,
        'bgcolor': '#fff',
        'transition': 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          borderColor: border.dark,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: border.radius.md,
            bgcolor: env.enabled ? xxllncColor.opGreen10 : xxllncColor.light40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <DnsIcon sx={{ fontSize: 20, color: env.enabled ? xxllncColor.green100 : xxllncColor.dark40 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.25 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              {env.display_name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                color: 'text.disabled',
                fontSize: '0.75rem',
                bgcolor: xxllncColor.light40,
                px: 0.75,
                py: 0.125,
                borderRadius: border.radius.sm / 2,
              }}
            >
              {env.name}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              color: 'text.secondary',
              fontSize: '0.8rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {truncateUrl(env.base_url)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, ml: 2 }}>
        <Chip
          label={env.enabled ? translate('data.envStatusActive') : translate('data.envStatusDisabled')}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: '0.75rem',
            bgcolor: env.enabled ? xxllncColor.opGreen10 : xxllncColor.light40,
            color: env.enabled ? xxllncColor.green100 : xxllncColor.dark60,
            border: 'none',
          }}
        />
        <IconButton size="small" onClick={() => onEdit(env)} sx={{ color: 'text.secondary' }}>
          <EditIcon fontSize="small" />
        </IconButton>
        <DeleteWithConfirmButton
          resource="application_environment"
          record={env}
          confirmTitle={translate('delete.confirmTitle')}
          confirmContent={translate('data.deleteEnvironmentConfirm')}
          mutationOptions={{ onSuccess: () => onDeleted() }}
          redirect={false}
        />
      </Box>
    </Box>
  )
}

const ApplicationEnvironmentTab: FC = () => {
  const record = useRecordContext<ApplicationOut>()
  const translate = useTranslate()
  const refresh = useRefresh()
  const [createOpen, setCreateOpen] = useState(false)
  const [editEnvironment, setEditEnvironment] = useState<ApplicationEnvironmentOut | null>(null)

  const { data, isLoading } = useGetList<ApplicationEnvironmentOut>(
    'application_environment',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'display_name', order: 'ASC' },
      // eslint-disable-next-line camelcase
      filter: { application_id: record?.id },
    },
    { enabled: !!record?.id },
  )

  if (!record?.id) return null
  if (isLoading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {translate('data.environmentAuthHelp')}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{
            borderRadius: border.button.radius,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {translate('data.addEnvironment')}
        </Button>
      </Box>

      {data && data.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {data.map((env) => (
            <EnvironmentCard
              key={env.id}
              env={env}
              onEdit={setEditEnvironment}
              onDeleted={() => refresh()}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            borderRadius: border.card.radius,
            border: `1px dashed ${border.dark}`,
            bgcolor: xxllncColor.light60,
          }}
        >
          <DnsIcon sx={{ fontSize: 36, color: xxllncColor.dark20, mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {translate('data.noEnvironments')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {translate('data.noEnvironmentsHint')}
          </Typography>
        </Box>
      )}

      <CreateEnvironmentDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        applicationId={record.id}
        authType={record.auth_type}
      />

      <EditEnvironmentDialog
        open={!!editEnvironment}
        onClose={() => setEditEnvironment(null)}
        environment={editEnvironment}
        applicationId={record.id}
        authType={record.auth_type}
      />
    </Box>
  )
}

export default ApplicationEnvironmentTab
